import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'

import LayoutContainer from 'components/Common/LayoutContainer'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineGuidedtoursIcon from 'LuxKit/Icons/line/LineGuidedtoursIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LineTheatreIcon from 'LuxKit/Icons/line/LineTheatreIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineWellnessIcon from 'LuxKit/Icons/line/LineWellnessIcon'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import {
  EXP_CATEGORY_DAY_TRIPS_ID,
  EXP_CATEGORY_FOOD_AND_DRINK_ID,
  EXP_CATEGORY_GIFT_INSPIRATION,
  EXP_CATEGORY_GUIDED_TOURS_ID,
  EXP_CATEGORY_LUX_EXCLUSIVE_ID,
  EXP_CATEGORY_LUX_PLUS_ID,
  EXP_CATEGORY_SHOWS_AND_EVENTS_ID,
  EXP_CATEGORY_TICKETS_AND_PASSES_ID,
  EXP_CATEGORY_TOP_ACTIVITIES_ID,
  EXP_CATEGORY_WELLNESS_AND_SPA_ID,
} from 'constants/experience'
import useExperienceHomepagePlace from 'components/Experiences/hooks/useExperienceHomepagePlace'
import useMostPopulatedSearchExperienceFacets from 'components/Experiences/hooks/useMostPopulatedSearchExperienceFacets'
import type { CategoryData } from 'lib/experiences/experiencesHomepageUtils'
import { getSearchThemeTitle } from 'components/Experiences/hooks/useExperienceThemes'
import { getExperienceSearchPath } from 'lib/experiences/experienceUtils'
import { LUX_PLUS_MEMBER_PRICES_FILTER } from 'lib/experiences/luxPlusFeatureFilterUtils'
import { take, fillArray } from 'lib/array/arrayUtils'

import ExperiencesHomepageBrowseCategoryChipSkeleton from './ExperiencesHomepageBrowseCategoryChipSkeleton'
import { ExperiencesHomepageCategoryStripToggleButton } from './ExperiencesHomepageCategoryStripToggle'
import { useExperiencesHomepageUpliftTracking } from './experiencesHomepageUpliftAnalytics'

const MAX_HOMEPAGE_CATEGORIES = 9
const CATEGORY_SKELETON_COUNT = 8
const CATEGORY_CHIP_SKELETON_WIDTHS = [148, 132, 160, 124, 152, 136] as const

const PARENT_CATEGORY_ICON_MAP: Record<number, ReactNode> = {
  [EXP_CATEGORY_FOOD_AND_DRINK_ID]: <LineRestaurantIcon />,
  [EXP_CATEGORY_TOP_ACTIVITIES_ID]: <LineHotAirBalloonIcon />,
  [EXP_CATEGORY_TICKETS_AND_PASSES_ID]: <LineTicketIcon />,
  [EXP_CATEGORY_SHOWS_AND_EVENTS_ID]: <LineTheatreIcon />,
  [EXP_CATEGORY_WELLNESS_AND_SPA_ID]: <LineWellnessIcon />,
  [EXP_CATEGORY_DAY_TRIPS_ID]: <LinePlaneDepartureIcon />,
  [EXP_CATEGORY_LUX_EXCLUSIVE_ID]: <LineStarIcon />,
  [EXP_CATEGORY_GUIDED_TOURS_ID]: <LineGuidedtoursIcon />,
  [EXP_CATEGORY_GIFT_INSPIRATION]: <LineGiftIcon />,
  [EXP_CATEGORY_LUX_PLUS_ID]: <LineAwardIcon />,
}

const DEFAULT_PARENT_CATEGORY_ICON = <LineStarIcon />

function getCategorySearchUrl(place: App.Place, categoryData: CategoryData) {
  const isLuxPlusCategory = categoryData.parent.id === EXP_CATEGORY_LUX_PLUS_ID
  return getExperienceSearchPath(place, {
    categories: isLuxPlusCategory ? undefined : [categoryData.parent.id.toString()],
    luxPlusFeatures: isLuxPlusCategory ? [LUX_PLUS_MEMBER_PRICES_FILTER] : undefined,
  })
}

const messages = defineMessages({
  sectionTitle: {
    id: 'YBgT9WoSQHAP9RRDTcEe',
    defaultMessage: 'Browse by <i>category</i>',
    description: 'Section heading for the browse by category chip strip on the Experiences homepage uplift',
  },
  viewAllCta: {
    id: 'MA5OZE_si8-qksbz-im8',
    defaultMessage: 'View all',
    description: 'Header tertiary call-to-action linking to all experience categories for the customer location on homepage uplift',
  },
  offerCount: {
    id: 'leplgcfMJGk4itEkhtnP',
    defaultMessage: '{count, plural, one {# offer} other {# offers}}',
    description: 'Offer count subtitle shown on each parent category chip in the Experiences homepage uplift browse strip',
  },
})

const Title = styled(Heading)`
  flex: 1 1 auto;
  min-inline-size: 0;
  text-align: start;
`

function ExperiencesHomepageBrowseByCategory() {
  const intl = useIntl()
  const { place, placeIdentity } = useExperienceHomepagePlace()
  const [trackingRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })
  const trackInteraction = useExperiencesHomepageUpliftTracking('browse_by_category')

  const { primaryCategories, fetching } = useMostPopulatedSearchExperienceFacets(placeIdentity, {
    useParentCategoryCounts: true,
  })

  const searchUrl = useMemo(() => (place ? getExperienceSearchPath(place) : ''), [place])

  const categories = useMemo(() => {
    if (!place) return []
    return take(
      primaryCategories.filter(category => config.BRAND === LUXURY_ESCAPES || category.parent.id !== EXP_CATEGORY_LUX_PLUS_ID),
      MAX_HOMEPAGE_CATEGORIES,
    )
      .map((category) => ({
        key: String(category.parent.id),
        title: getSearchThemeTitle(intl, category.parent.id, category.parent.name),
        subtitle: intl.formatMessage(messages.offerCount, { count: category.count }),
        startIcon: PARENT_CATEGORY_ICON_MAP[category.parent.id] ?? DEFAULT_PARENT_CATEGORY_ICON,
        to: getCategorySearchUrl(place, category),
      }))
  }, [intl, place, primaryCategories])

  const viewAllLabel = intl.formatMessage(messages.viewAllCta)

  useEffect(() => {
    if (inView && !fetching && categories.length > 0) {
      trackInteraction('browse_by_category', 'impression')
    }
  }, [categories.length, fetching, inView, trackInteraction])

  const handleViewAllClick = useCallback(() => {
    trackInteraction('browse_by_category', 'view_all_clicked')
  }, [trackInteraction])

  const handleCategoryClick = useCallback((position: number) => {
    trackInteraction('browse_by_category', 'tile_clicked', position)
  }, [trackInteraction])

  return (
    <LayoutContainer ref={trackingRef}>
      <Group direction="vertical" gap={16}>
        <Group
          direction="horizontal"
          verticalAlign="center"
          gap={20}
          tabletGap={16}
          tabletHorizontalAlign="space-between"
          fullWidth
        >
          <Title variant="heading3">
            {intl.formatMessage(messages.sectionTitle, {
              i: (chunks: ReactNode) => <i key="italic-category">{chunks}</i>,
            })}
          </Title>
          <CSSBreakpoint max="mobile">
            <TextButton
              kind="tertiary"
              size="small"
              horizontalOutdent="end"
              to={searchUrl}
              onClick={handleViewAllClick}
            >
              {viewAllLabel}
            </TextButton>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <TextButton
              kind="tertiary"
              size="medium"
              horizontalOutdent="end"
              to={searchUrl}
              onClick={handleViewAllClick}
            >
              {viewAllLabel}
            </TextButton>
          </CSSBreakpoint>
        </Group>
        {fetching && (
          <CardCarousel pageSize="auto" gap={12} snap="start" desktopGutterSize={0} aria-busy="true">
            {fillArray(CATEGORY_SKELETON_COUNT).map((id) => (
              <ExperiencesHomepageBrowseCategoryChipSkeleton
                key={id}
                width={CATEGORY_CHIP_SKELETON_WIDTHS[id]}
              />
            ))}
          </CardCarousel>
        )}
        {!fetching && categories.length > 0 && (
          <CardCarousel pageSize="auto" gap={12} snap="start" desktopGutterSize={0}>
            {categories.map((category, index) => (
              <ExperiencesHomepageCategoryStripToggleButton
                key={category.key}
                kind="secondary"
                orientation="horizontal"
                title={category.title}
                subtitle={category.subtitle}
                startIcon={category.startIcon}
                to={category.to}
                onClick={() => handleCategoryClick(index + 1)}
              />
            ))}
          </CardCarousel>
        )}
      </Group>
    </LayoutContainer>
  )
}

export default ExperiencesHomepageBrowseByCategory
